import { useEffect, useRef, memo } from 'react';
import { EditorView, keymap, ViewUpdate, lineNumbers, highlightActiveLineGutter, highlightSpecialChars, drawSelection, dropCursor, rectangularSelection, crosshairCursor, highlightActiveLine } from '@codemirror/view';
import { EditorState, Extension } from '@codemirror/state';
import { css } from '@codemirror/lang-css';
import { oneDark } from '@codemirror/theme-one-dark';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { bracketMatching, indentOnInput, syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';

// Basic setup extensions (replaces 'codemirror' basicSetup which has module resolution issues)
const basicSetup: Extension[] = [
  lineNumbers(),
  highlightActiveLineGutter(),
  highlightSpecialChars(),
  history(),
  drawSelection(),
  dropCursor(),
  EditorState.allowMultipleSelections.of(true),
  indentOnInput(),
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
  bracketMatching(),
  closeBrackets(),
  rectangularSelection(),
  crosshairCursor(),
  highlightActiveLine(),
  keymap.of([
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...historyKeymap,
  ]),
];

interface CssEditorProps {
  /** The CSS content to display */
  value: string;
  /** Callback when content changes */
  onChange: (value: string) => void;
  /** Height of the editor */
  height?: string;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Whether the editor is read-only */
  readOnly?: boolean;
}

/**
 * A CodeMirror-based CSS editor component with syntax highlighting.
 * Uses the One Dark theme for a modern developer experience.
 */
export const CssEditor = memo(function CssEditor({
  value,
  onChange,
  height = '300px',
  placeholder = '/* Add your custom CSS here */\n\n.my-class {\n  color: red;\n}',
  readOnly = false,
}: CssEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Initialize editor
  useEffect(() => {
    if (!editorRef.current) return;

    const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
      if (update.docChanged) {
        const newValue = update.state.doc.toString();
        onChangeRef.current(newValue);
      }
    });

    const state = EditorState.create({
      doc: value || '',
      extensions: [
        basicSetup,
        css(),
        oneDark,
        updateListener,
        EditorView.lineWrapping,
        EditorState.readOnly.of(readOnly),
        EditorView.theme({
          '&': { height },
          '.cm-scroller': { overflow: 'auto' },
          '.cm-content': { minHeight: '100px' },
        }),
      ],
    });

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Only run on mount/unmount - value changes are handled separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, readOnly]);

  // Update content when value prop changes externally
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const currentContent = view.state.doc.toString();
    if (currentContent !== value) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentContent.length,
          insert: value || '',
        },
      });
    }
  }, [value]);

  return (
    <div className="tw-css-editor tw:rounded-sm tw:overflow-hidden tw:border tw:border-gray-700">
      <div ref={editorRef} />
      {!value && (
        <div className="tw:text-gray-500 tw:text-sm tw:p-2 tw:absolute tw:pointer-events-none tw:hidden">
          {placeholder}
        </div>
      )}
    </div>
  );
});

export default CssEditor;
